//
//  Header.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the Header component.
//

import {
    Avatar,
    ActionIcon,
    Box,
    Burger,
    Divider,
    Group,
    Image,
    Menu,
    Text,
    UnstyledButton
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link as RouterLink } from "react-router-dom";

import AppSettings from "src/AppSettings";
import TrogLogoDark from "src/assets/img/trog_logo_dark.png";
import TrogLogoLight from "src/assets/img/trog_logo_light.png";
import Icon from "src/components/common/Icon";
import type { ILayoutLink } from "src/contexts/layouts/LayoutContext";
import { ApplicationTheme } from "src/contexts/preferences/UserPreferencesContext";
import useCurrentUser from "src/hooks/authentication/useCurrentUser";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useUserPreferences from "src/hooks/preferences/useUserPreferences";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

import {
    HelpAction,
    ReportBugAction,
    SearchAction,
    ThemeToggleAction
} from "../shared/HeaderActions";
import PreferencesModal from "../shared/PreferencesModal";
import NavMenu from "./NavMenu";

export const modernHeaderHeight = 45;

/**
 * Defines the inputs for this navigation presentation.
 */
interface IHeaderProps {
    menuItems?: ILayoutLink[];
    mobileOpened: boolean;
    toggleMobile: () => void;
    hasDesktopSidebar: boolean;
    desktopSidebarWidth: number;
    sidebarCollapsed: boolean;
    toggleSidebar: () => void;
    hasPinnedChecklist?: boolean;
    openPinnedChecklist?: () => void;
}

/**
 * Uses the first characters of at most two whitespace-separated names, uppercased.
 *
 * Postconditions: Missing or blank names yield a question mark.
 */
const getInitials = (fullName?: string) => {
    const names = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
    return (
        names
            .slice(0, 2)
            .map(name => name[0]?.toUpperCase())
            .join("") || "?"
    );
};

/**
 * Renders responsive navigation controls, branding, account actions and preferences.
 *
 * Postconditions: The search action appears only when the permitted menu includes /search.
 * Sidebar and mobile toggles invoke the supplied actions; preferences visibility is local state.
 */
const Header = ({
    menuItems = [],
    mobileOpened,
    toggleMobile,
    hasDesktopSidebar,
    desktopSidebarWidth,
    sidebarCollapsed,
    toggleSidebar,
    hasPinnedChecklist,
    openPinnedChecklist
}: IHeaderProps) => {
    const user = useCurrentUser();
    const permissions = useCurrentUserPermissions();
    const { preferences } = useUserPreferences();
    const [preferencesOpened, preferencesHandlers] = useDisclosure(false);
    const searchAllowed = filterLayoutLinksByPermission(permissions, menuItems).some(
        item => item.href === "/search"
    );

    return (
        <Group
            h="100%"
            px={{ base: "md", sm: hasDesktopSidebar ? 0 : "md" }}
            gap="md"
            wrap="nowrap"
        >
            <Burger
                hiddenFrom="sm"
                opened={mobileOpened}
                onClick={toggleMobile}
                size="sm"
                aria-label="Toggle navigation"
            />
            {hasDesktopSidebar && (
                <Group
                    visibleFrom="sm"
                    h="100%"
                    w={desktopSidebarWidth}
                    justify="center"
                    gap={0}
                    wrap="nowrap"
                >
                    <Burger
                        opened={!sidebarCollapsed}
                        onClick={toggleSidebar}
                        size="sm"
                        aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
                    />
                </Group>
            )}
            <Group
                gap="sm"
                wrap="nowrap"
            >
                <UnstyledButton
                    component={RouterLink}
                    to="/dashboard"
                    aria-label="Dashboard"
                >
                    <Image
                        src={
                            preferences.theme === ApplicationTheme.Dark
                                ? TrogLogoDark
                                : TrogLogoLight
                        }
                        alt="TROG Cancer Research"
                        h={40}
                        w="auto"
                        fit="contain"
                    />
                </UnstyledButton>
                <Text
                    fz={{ base: "md", lg: "lg" }}
                    fw={700}
                    style={{ whiteSpace: "nowrap" }}
                >
                    Trial Management
                </Text>
            </Group>
            <Group
                gap={0}
                align="stretch"
                justify="flex-end"
                ml="auto"
                mr="sm"
                style={{
                    alignSelf: "stretch"
                }}
            >
                <NavMenu menuItems={menuItems} />
                <Group
                    h="100%"
                    gap={0}
                    wrap="nowrap"
                >
                    <Divider orientation="vertical" />
                    {hasPinnedChecklist && (
                        <ActionIcon
                            hiddenFrom="sm"
                            variant="subtle"
                            size={modernHeaderHeight}
                            aria-label="Open pinned QA program checklist"
                            onClick={openPinnedChecklist}
                        >
                            <Icon icon={AppSettings.icons.models.qaProgramChecklist} />
                        </ActionIcon>
                    )}
                    {searchAllowed && <SearchAction size={modernHeaderHeight} />}
                    <HelpAction size={modernHeaderHeight} />
                    <ReportBugAction size={modernHeaderHeight} />
                    <ThemeToggleAction size={modernHeaderHeight} />
                    {user && (
                        <Menu
                            position="bottom-end"
                            width={240}
                            withinPortal
                        >
                            <Menu.Target>
                                <UnstyledButton
                                    aria-label="User menu"
                                    w={modernHeaderHeight}
                                    h={modernHeaderHeight}
                                    style={{
                                        display: "grid",
                                        placeItems: "center"
                                    }}
                                >
                                    <Avatar
                                        color="primary"
                                        size="sm"
                                    >
                                        {getInitials(user.fullName)}
                                    </Avatar>
                                </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Label>{user.fullName}</Menu.Label>
                                <Menu.Item
                                    leftSection={<Icon icon={AppSettings.icons.preferences} />}
                                    onClick={preferencesHandlers.open}
                                >
                                    Preferences
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item
                                    component="a"
                                    href="/auth/logout"
                                    leftSection={<Icon icon={AppSettings.icons.logout} />}
                                >
                                    Logout
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    )}
                </Group>
            </Group>
            <PreferencesModal
                opened={preferencesOpened}
                onClose={preferencesHandlers.close}
            />
        </Group>
    );
};

export default Header;
